/**
 * 정렬 배치(justified rows) — 줄마다 가로 폭을 꽉 채우고, 한 줄 안의 카드는 실물 높이를 같게 맞춘다.
 *
 * 카드를 차례로 줄에 담다가 «목표 높이로 늘어놓은 폭»이 줄 폭을 넘는 순간 그 줄을 닫고,
 * 줄 폭에 딱 맞게 높이를 다시 잡는다(비율대로 폭이 나뉜다). 마지막 줄이 덜 찼으면 목표 높이 그대로 두고 왼쪽부터 편다.
 */

export type JustifiedItem = { key: string; ratio: number };

export type JustifiedRow = {
  /** 이 줄 실물 높이(px) */
  height: number;
  /** 줄 폭을 꽉 채웠나 — 마지막 덜 찬 줄은 `false` */
  complete: boolean;
  items: { key: string; width: number }[];
};

/**
 * @param items 차례대로의 카드와 실물 비율(가로÷세로)
 * @param width 줄 폭(px)
 * @param targetHeight 목표 실물 높이(px)
 * @param gap 카드 사이 간격(px)
 * @param maxHeight 줄 높이 상한(px) — 가로형이 몰려 줄이 너무 커지지 않게. 넘으면 그 높이로 두고 줄이 덜 찬다
 * @param maxPerRow 한 줄에 담을 수 있는 최대 장 수 — 목표 높이로는 더 들어갈 때도 여기서 줄을 닫는다
 */
export function layoutJustifiedRows(
  items: JustifiedItem[],
  width: number,
  targetHeight: number,
  gap: number,
  maxHeight: number = targetHeight * 1.5,
  maxPerRow: number = 0,
): JustifiedRow[] {
  if (width <= 0) return [];
  const rows: JustifiedRow[] = [];
  let row: JustifiedItem[] = [];
  let ratioSum = 0;

  const close = (complete: boolean) => {
    const gaps = gap * (row.length - 1);
    const fitted = (width - gaps) / ratioSum;
    const height = complete ? Math.min(fitted, maxHeight) : Math.min(targetHeight, fitted);
    rows.push({ height, complete, items: row.map((it) => ({ key: it.key, width: it.ratio * height })) });
    row = [];
    ratioSum = 0;
  };

  items.forEach((it) => {
    row.push(it);
    ratioSum += it.ratio;
    const natural = ratioSum * targetHeight + gap * (row.length - 1);
    if (natural >= width || (maxPerRow > 0 && row.length >= maxPerRow)) close(true);
  });
  if (row.length > 0) close(false);
  return rows;
}
